import type { GridRowSelectionModel } from '@mui/x-data-grid-premium';
import type { WhereCondition } from '../services/api';

// ── Selection ────────────────────────────────────────────────────────────────

/**
 * Resolve the concrete list of selected row ids from a v8 GridRowSelectionModel.
 * Handles both `include` (ids are selected) and `exclude` (select-all minus ids)
 * — the latter needs the currently-visible rows to compute the complement.
 */
export function resolveSelectedIds(sel: GridRowSelectionModel, rows: Array<{ id: string }>): string[] {
  if (sel.type === 'exclude') return rows.filter((r) => !sel.ids.has(r.id)).map((r) => String(r.id));
  return Array.from(sel.ids).map(String);
}

export const emptySelection: GridRowSelectionModel = { type: 'include', ids: new Set() };

// ── Client-side filtering ────────────────────────────────────────────────────

export interface EntityFilterState {
  search: string;
  /** Status values to show; empty means no status filter at all. */
  statuses: string[];
  tagIds: string[];
}

// ── Server-side filtering ────────────────────────────────────────────────────

/** Search, status and tag filters for the /query endpoints, so they apply past the current page. */
export function buildEntityWhere(
  entity: string,
  { search, statuses, tagIds }: EntityFilterState,
  searchFields: string[],
  extra: WhereCondition[] = [],
): WhereCondition | undefined {
  const conds: WhereCondition[] = [...extra];

  if (statuses.length === 1) conds.push({ [entity]: { status: { op: '=', value: statuses[0] } } });
  else if (statuses.length > 1) conds.push({ [entity]: { status: { op: 'in', value: statuses } } });
  if (tagIds.length) conds.push({ [entity]: { tag_ids: { op: 'arrOverlap', value: tagIds } } });

  const q = search.trim();
  if (q) {
    const like = searchFields.map((f) => ({ [entity]: { [f]: { op: 'ilike', value: `%${q}%` } } }));
    conds.push(like.length === 1 ? like[0] : { $or: like });
  }

  if (!conds.length) return undefined;
  return conds.length === 1 ? conds[0] : { $and: conds };
}

// ── Bulk update ──────────────────────────────────────────────────────────────

export type TagMode = 'set' | 'add' | 'remove';

export interface BulkResult {
  /** Fields set verbatim on every selected row (status, caps, …). */
  updates: Record<string, unknown>;
  /** Tag change, merged per-row against each row's current tag_ids. */
  tags?: { mode: TagMode; ids: string[] };
}

/** True when the result would change nothing (nothing enabled). */
export function isEmptyBulk(r: BulkResult): boolean {
  return Object.keys(r.updates).length === 0 && !r.tags;
}

/**
 * Apply a bulk edit by looping the entity's own `update(id, patch)` — mirrors the
 * existing archive/unarchive pattern (no backend endpoint needed). Tag changes are
 * merged against each row's current tag_ids so `add`/`remove` are non-destructive.
 */
export async function applyBulkUpdate<T extends { id: string; tag_ids?: string[] }>(
  updateFn: (id: string, patch: Record<string, unknown>) => Promise<unknown>,
  ids: string[],
  rows: T[],
  result: BulkResult,
): Promise<void> {
  const byId = new Map(rows.map((r) => [r.id, r]));
  await Promise.all(
    ids.map((id) => {
      const patch: Record<string, unknown> = { ...result.updates };
      if (result.tags) {
        const cur = byId.get(id)?.tag_ids ?? [];
        if (result.tags.mode === 'set') patch.tag_ids = result.tags.ids;
        else if (result.tags.mode === 'add') patch.tag_ids = Array.from(new Set([...cur, ...result.tags.ids]));
        else patch.tag_ids = cur.filter((t) => !result.tags!.ids.includes(t));
      }
      return updateFn(id, patch);
    }),
  );
}

/** The row actions stay in view on a wide screen; on a phone a pin would leave the scrolling columns no room. Read at mount. */
export const actionsPinnedRight = () =>
  (typeof window !== 'undefined' && window.innerWidth >= 900 ? { pinnedColumns: { right: ['actions'] } } : undefined);
